import { formatDisplayValue, formatEmployeeCountDisplay, formatPercentValue, isEmptyDisplayValue } from './formatters'

const DEFAULT_GENDER_LABELS = {
  all: '全労働者',
  regular: '正規雇用労働者',
  nonRegular: 'パート・有期労働者'
}

/**
 * @param {string | undefined | null} segment
 * @returns {string}
 */
function formatSegmentName(segment) {
  const trimmed = (segment ?? '').trim()
  return trimmed || '単一セグメント'
}

/**
 * @param {{ Segment?: string; RegularEmployee?: number; TemporaryEmployee?: number }[]} rows
 * @returns {{ rows: import('./types').SegmentRow[]; total: import('./types').SegmentTotal }}
 */
function mapSegmentRows(rows) {
  const mappedRows = (rows ?? []).map((row) => ({
    name: formatSegmentName(row.Segment),
    count: formatEmployeeCountDisplay(row.RegularEmployee, row.TemporaryEmployee)
  }))

  const regularTotal = (rows ?? []).reduce((sum, row) => sum + (Number(row.RegularEmployee) || 0), 0)
  const temporaryTotal = (rows ?? []).reduce((sum, row) => sum + (Number(row.TemporaryEmployee) || 0), 0)

  return {
    rows: mappedRows,
    total: {
      name: '合計',
      count: formatEmployeeCountDisplay(regularTotal, temporaryTotal)
    }
  }
}

/**
 * @param {object} item IF02103 レスポンス Items 要素
 * @returns {import('./types').EmployeeStatusYearData}
 */
export function mapApiItemToYearData(item) {
  const consolidated = mapSegmentRows(item.GroupCompanyEmployeeCount)
  const segmentBreakdown = mapSegmentRows(item.SubmittingCompanyEmployeeCount)
  const config = item.Config ?? {}

  const submitter = (item.SubmittingCompanyStatus ?? []).map((row) => ({
    name: row.CompName || '',
    kind: row.CompanyCategory === '提出会社' ? 'submit' : 'sub',
    kindLabel: row.CompanyCategory || '',
    employees: isEmptyDisplayValue(row.RegularEmployee) ? 0 : Number(row.RegularEmployee) || 0,
    employeesDisplay: formatEmployeeCountDisplay(row.RegularEmployee, row.TemporaryEmployee),
    age: formatDisplayValue(row.AverageAge),
    tenure: formatDisplayValue(row.AverageYearsOfService),
    salary: isEmptyDisplayValue(row.AverageAnnualSalary) ? null : Number(row.AverageAnnualSalary),
    rate: formatPercentValue(row.AverageAnnualSalaryChangeRate)
  }))

  const diversity = (item.GenderStatisticsList ?? []).map((row) => ({
    name: row.CompName || '',
    mgmt: formatPercentValue(row.FemaleManagerRatioValue),
    male: [
      formatPercentValue(row.MaleParentalLeaveRateAll),
      formatPercentValue(row.MaleParentalLeaveRateRegular),
      formatPercentValue(row.MaleParentalLeaveRatePartTime)
    ],
    wage: [
      formatPercentValue(row.WageGapPercentAll),
      formatPercentValue(row.WageGapPercentRegular),
      formatPercentValue(row.WageGapPercentPartTime)
    ]
  }))

  return {
    fiscalYear: item.FiscalYear || '',
    snapshotDate: item.SnapshotDate || '',
    genderLabels: {
      all: config.GenderStatsAll || DEFAULT_GENDER_LABELS.all,
      regular: config.GenderStatsRegular || DEFAULT_GENDER_LABELS.regular,
      nonRegular: config.GenderStatsNonRegular || DEFAULT_GENDER_LABELS.nonRegular
    },
    consolidated,
    submitter,
    segmentBreakdown,
    diversity
  }
}

/**
 * @param {object[]} items
 * @returns {{ yearOptions: string[]; dataByPeriod: Record<string, import('./types').EmployeeStatusYearData> }}
 */
export function mapApiItemsToEmployeeStatusData(items) {
  const dataByPeriod = {}
  const yearOptions = []

  for (const item of items ?? []) {
    const fiscalPeriod = item.FiscalPeriod
    if (!fiscalPeriod) {
      continue
    }
    dataByPeriod[fiscalPeriod] = mapApiItemToYearData(item)
    yearOptions.push(fiscalPeriod)
  }

  return { yearOptions, dataByPeriod }
}
